import React, { useEffect, useState } from "react";
import { URL_START } from '../../data/config';

const Supplements = ({ map, openWindows, setOpenWindows, setWindowSize }) => {
    const [isVisible, setIsVisible] = useState(true);
    const [data, setData] = useState(null);

    // Helper: convert "lat lon,lat lon,..." to a GeoJSON Polygon coordinates array
    const parseCoordinates = (coordStr) => {
        const coords = coordStr.split(',').map(pair => {
            const [lat, lon] = pair.trim().split(' ').map(Number);
            return [lon, lat]; // Mapbox uses [lng, lat]
        });
        return [coords]; // GeoJSON Polygon expects an array of rings
    };

    useEffect(() => {
        if (!map) return;

        const fetchData = async () => {
            try {
                const response = await fetch(`${URL_START}/api/areas`);
                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
                const apiData = await response.json();

                // Convert your API data to proper GeoJSON features
                const features = apiData.features.map(f => ({
                    type: "Feature",
                    properties: {
                        designator: f.designator,
                        isactive: f.isactive,
                        type: f.type
                    },
                    geometry: {
                        type: "Polygon",
                        coordinates: parseCoordinates(f.coordinates)
                    }
                }));

                const geojsonData = {
                    type: "FeatureCollection",
                    features
                };

                setData(geojsonData);
                addDataToMap(map, geojsonData);
            } catch (error) {
                console.error(`Failed fetching Supplements:`, error);
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 8 * 60 * 1000);
        return () => clearInterval(interval);
    }, [map]);

    const addDataToMap = (map, geojsonData) => {
        if (!map.getSource("supplements")) {
            map.addSource("supplements", { type: "geojson", data: geojsonData });
        } else {
            map.getSource("supplements").setData(geojsonData);
        }

        if (!map.getLayer("supplements-layer")) {
            map.addLayer({
                id: "supplements-layer",
                type: "line",
                source: "supplements",
                paint: {
                    "line-color": [
                        "case",
                        ["==", ["get", "type"], "D"], "darkorange",
                        ["==", ["get", "type"], "R"], "red",
                        ["==", ["get", "type"], "P"], "red",
                        "gray"
                    ],
                    "line-width": 1,
                    "line-dasharray": [
                        "case",
                        ["==", ["get", "isactive"], false], [2, 2],
                        [1, 0]
                    ],
                    "line-opacity": 1
                }
            });
            map.setLayoutProperty("supplements-layer", "visibility", isVisible ? "visible" : "none");
        }

        if (!map.getLayer("supplements-layer-fill")) {
            map.addLayer({
                id: "supplements-layer-fill",
                type: "fill",
                source: "supplements",
                paint: {
                    "fill-color": [
                        "case",
                        ["==", ["get", "type"], "D"], "darkorange",
                        ["==", ["get", "type"], "R"], "darkred",
                        ["==", ["get", "type"], "P"], "darkred",
                        "gray"
                    ],
                    "fill-opacity": [
                        "case",
                        ["==", ["get", "isactive"], false], 0,
                        0.3
                    ]
                }
            });
            map.setLayoutProperty("supplements-layer-fill", "visibility", isVisible ? "visible" : "none");
        }

        if (!map.getLayer("supplements-labels")) {
            map.addLayer({
                id: "supplements-labels",
                type: "symbol",
                source: "supplements",
                filter: ["==", ["get", "isactive"], true],
                layout: {
                    "text-field": ["get", "designator"],
                    "text-size": 9,
                    "text-font": ["Open Sans Bold"],
                    "text-allow-overlap": true,
                },
                paint: {
                    "text-color": [
                        "case",
                        ["==", ["get", "type"], "D"], "brown",
                        ["==", ["get", "type"], "R"], "darkred",
                        ["==", ["get", "type"], "P"], "darkred",
                        "darkgray"
                    ],
                    "text-halo-color": "white",
                    "text-halo-width": 1
                },
                minzoom: 5
            });

            map.on("mouseenter", 'supplements-labels', () => {
                map.getCanvas().style.cursor = "pointer";
            });

            map.on("mouseleave", 'supplements-labels', () => {
                map.getCanvas().style.cursor = "default";
            });

            if (map.getLayer("aprons")) {
                const belowLayer = "aprons";
                const layersToMove = [
                    "supplements-layer-fill",
                    "supplements-layer",
                    "supplements-labels"
                ];

                layersToMove.forEach(layerId => {
                    if (map.getLayer(layerId)) {
                        try {
                            map.moveLayer(layerId, belowLayer);
                        } catch (e) {
                            console.warn(`Failed to move ${layerId}:`, e);
                        }
                    }
                });
            }

            // Click: fetch NOTAM details from backend
            map.off('click', 'supplements-labels'); // remove existing
            map.on('click', 'supplements-labels', async (e) => {
                const feature = e.features[0];
                const designator = feature.properties.designator;

                let frmtdnotammsg = 'No NOTAM data';
                try {
                    const res = await fetch(`${URL_START}/api/areaDetails?designator=${designator}`);
                    if (res.ok) {
                        const json = await res.json();
                        frmtdnotammsg = json.frmtdnotammsg || 'No NOTAM data';
                    }
                } catch (err) {
                    console.error('Error fetching NOTAM details:', err);
                }

                const lowerMatch = frmtdnotammsg.match(/F\)\s*([^\s]+)/);
                const upperMatch = frmtdnotammsg.match(/G\)\s*([^\s]+)/);
                const lower = lowerMatch ? lowerMatch[1] : null;
                const upper = upperMatch ? upperMatch[1] : null;

                const content = (
                    <div className="p-2 monospace-font text-sm overflow-y-auto mb-6" style={{ userSelect: 'text' }}>
                        <div className="flex gap-2">
                            <div className="pilotdepdest flex-1">{designator}</div>
                            {lower && upper && (
                                <div className="pilotdepdest flex-1">
                                    {lower} - {upper}
                                </div>
                            )}
                        </div>

                        <div>
                            <p className="pl-[2px]">RAW NOTAM</p>
                            <div className="flightplan text-xs mb-2"> {frmtdnotammsg} </div>
                        </div>

                    </div>
                );

                setOpenWindows(prev => {
                    const existingIndex = prev.findIndex(win => win.title === designator);
                    if (existingIndex !== -1) {
                        const updated = [...prev];
                        updated[existingIndex] = { ...updated[existingIndex], content };
                        return updated;
                    } else {
                        return [...prev, { id: `supplement-${designator}-${Date.now()}`, title: designator, content }];
                    }
                });
                setWindowSize(2);
            });

            map.setLayoutProperty("supplements-labels", "visibility", isVisible ? "visible" : "none");
        }
    };

    const toggleVisibility = () => {
        if (!map) return;
        const visibility = isVisible ? "none" : "visible";
        map.setLayoutProperty("supplements-layer", "visibility", visibility);
        map.setLayoutProperty("supplements-layer-fill", "visibility", visibility);
        map.setLayoutProperty("supplements-labels", "visibility", visibility);
        setIsVisible(!isVisible);
    };

    return (
        <button
            data-tooltip-id="map-buttons-tooltip"
            data-tooltip-content="Supplements (R/D areas)"
            className={`w-8 h-8 ${isVisible ? "bg-gray-600" : "bg-gray-400"} text-white flex items-center justify-center`}
            onClick={toggleVisibility}
        >
            <span role="img" aria-label="Supplements" className="text-sm">R/D</span>
        </button>
    );
};

export default Supplements;
